import { useEffect } from 'react';
import type { Map } from 'maplibre-gl';
import proj4 from 'proj4'; // *** AGGIUNTO import proj4 ***
import useGeoData from '../../../store/useGeoData';
import { useShallow } from 'zustand/react/shallow';
import type { BBox } from '../../../store/types'; // Importa il tipo BBox

// *** AGGIUNTO: Definizioni Proj4 (idealmente da un file utility) ***
// EPSG:4326 (WGS 84) - Lat/Lon
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
// EPSG:3857 (Web Mercator)
proj4.defs("EPSG:3857", "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs");


/**
 * Custom hook to handle zooming the map to the extent of a specific layer
 * triggered by the state change in the Zustand store.
 * @param map The MapLibre GL JS map instance.
 */
export const useMapZoomToLayer = (map: Map | null) => {
  // Select necessary state and actions from the store
  const { layerIdToZoomTo, getLayerById, clearZoomToLayerExtent } = useGeoData(
    useShallow((state) => ({
      layerIdToZoomTo: state.layerIdToZoomTo,
      getLayerById: state.getLayerById,
      clearZoomToLayerExtent: state.clearZoomToLayerExtent,
    }))
  );

  useEffect(() => {
    if (map && layerIdToZoomTo) {
      const layer = getLayerById(layerIdToZoomTo);

      // console.log(`[useMapZoomToLayer] Attempting to zoom to layer ID: ${layerIdToZoomTo}`, layer ? JSON.stringify(layer, null, 2) : 'Layer not found in store');

      if (layer) { // Controlla solo se il layer esiste
        try {
          let boundsToFit: BBox | null = null;
          // console.log(`[useMapZoomToLayer] Initial boundsToFit for layer ${layerIdToZoomTo}:`, boundsToFit);

          // PRIMA STRATEGIA: Usare layer.extent se disponibile e valido (dovrebbe essere già EPSG:4326)
          if (layer.extent && Array.isArray(layer.extent) && layer.extent.length === 4 && layer.extent.every(num => typeof num === 'number' && isFinite(num))) {
            // console.log(`[useMapZoomToLayer] Utilizzo di layer.extent pre-calcolato per layer ${layerIdToZoomTo}:`, JSON.stringify(layer.extent));
            boundsToFit = layer.extent;
          } else {
            // SECONDA STRATEGIA: Logica specifica per tipo di layer se layer.extent non è disponibile/valido
            // console.log(`[useMapZoomToLayer] layer.extent non disponibile o non valido per ${layerIdToZoomTo}. Procedo con logica specifica per tipo.`);
            if (layer.maplibreLayerType === 'wms' || layer.maplibreLayerType === 'raster') {
              // Gestione WMS/Raster: Prova a recuperare l'extent dalla sorgente MapLibre
              if (map && layer.maplibreSourceId) {
                const source = map.getSource(layer.maplibreSourceId);
                if (source && 'bounds' in source && Array.isArray(source.bounds) && source.bounds.length === 4 && source.bounds.every(num => typeof num === 'number')) {
                   const sourceBounds = source.bounds as BBox;
                   // console.log(`[useMapZoomToLayer] Raw source.bounds for layer ${layerIdToZoomTo} from MapLibre:`, JSON.stringify(sourceBounds));

                   let isCogLayer = false;
                   if (layer.sourceSpecification && (layer.sourceSpecification.type === 'raster' || layer.sourceSpecification.type === 'raster-dem')) {
                     const rasterOrDemSourceSpec = layer.sourceSpecification as import('../../../store/types').RasterSourceSpecification | import('../../../store/types').RasterDemSourceSpecification;
                     if (rasterOrDemSourceSpec.url?.startsWith('cog://')) {
                       isCogLayer = true;
                     }
                   }

                   if (isCogLayer) {
                     // console.log(`[useMapZoomToLayer] Layer ${layerIdToZoomTo} identificato come COG. Si assumono bounds (da source.bounds) in EPSG:4326.`);
                     if (Array.isArray(sourceBounds) && sourceBounds.length === 4 && sourceBounds.every(coord => typeof coord === 'number' && isFinite(coord))) {
                       boundsToFit = [...sourceBounds];
                     } else {
                       // console.warn(`[useMapZoomToLayer] Bounds (da source.bounds) per COG layer ${layerIdToZoomTo} non validi:`, JSON.stringify(sourceBounds));
                       boundsToFit = null;
                     }
                   } else {
                     // console.log(`[useMapZoomToLayer] Layer ${layerIdToZoomTo} (raster, non COG). Si tenta la trasformazione da EPSG:3857 per source.bounds.`);
                     try {
                       const [minLon, minLat] = proj4('EPSG:3857', 'EPSG:4326').forward([sourceBounds[0], sourceBounds[1]]);
                       const [maxLon, maxLat] = proj4('EPSG:3857', 'EPSG:4326').forward([sourceBounds[2], sourceBounds[3]]);
                       if (!isNaN(minLon) && !isNaN(minLat) && !isNaN(maxLon) && !isNaN(maxLat) && isFinite(minLon) && isFinite(minLat) && isFinite(maxLon) && isFinite(maxLat)) {
                         boundsToFit = [minLon, minLat, maxLon, maxLat];
                         // console.log(`[useMapZoomToLayer] Transformed source.bounds for layer ${layerIdToZoomTo} to EPSG:4326:`, JSON.stringify(boundsToFit));
                       } else {
                         // console.warn(`[useMapZoomToLayer] Trasformazione source.bounds da EPSG:3857 a EPSG:4326 ha prodotto coordinate non valide/infinite per layer ${layerIdToZoomTo}. Raw: ${JSON.stringify(sourceBounds)}`);
                         boundsToFit = null;
                       }
                     } catch (transformError) {
                       // console.error(`[useMapZoomToLayer] Errore trasformazione source.bounds da EPSG:3857 a EPSG:4326 per layer ${layerIdToZoomTo}. Raw: ${JSON.stringify(sourceBounds)}:`, transformError);
                       boundsToFit = null;
                     }
                   }
                } else {
                  // console.warn(`[useMapZoomToLayer] Impossibile recuperare un extent valido (source.bounds) dalla sorgente MapLibre '${layer.maplibreSourceId}' per il layer ${layerIdToZoomTo}. Source object:`, source);
                }
              }
            } else if (layer.sourceSpecification && layer.sourceSpecification.type === 'geojson' && typeof layer.sourceSpecification.data === 'string' && layer.sourceSpecification.data.startsWith('http')) {
              // CASO: GeoJSON da URL. layer.extent dovrebbe essere stato popolato dal dialogo.
              // Se siamo qui, significa che layer.extent non era valido/disponibile.
              // Non tentare di ricalcolare bbox dall'URL qui, perché fallirebbe di nuovo.
              // Lo zoom fallirà se layer.extent non è stato impostato correttamente.
              // console.warn(`[useMapZoomToLayer] Per GeoJSON da URL ${layerIdToZoomTo}, layer.extent non era valido. Lo zoom automatico probabilmente fallirà se non si usa il fallback a source.bounds (non applicabile per geojson).`);
            }
            // NON aggiungere qui un else if per calcolare bbox da layer.sourceSpecification.data se è un oggetto GeoJSON inline,
            // perché se i dati sono inline, AddVectorLayerDialog dovrebbe aver calcolato i bounds e messi in layer.extent.
            // Se layer.extent non c'è, c'è un problema a monte.
          }

          // Applica fitBounds se abbiamo trovato un extent valido
          if (boundsToFit) {
            // console.log(`[useMapZoomToLayer] boundsToFit for layer ${layerIdToZoomTo} before validation:`, JSON.stringify(boundsToFit));
            // Verifica se boundsToFit è un array valido di 4 numeri finiti
            if (Array.isArray(boundsToFit) && boundsToFit.length === 4 && boundsToFit.every(coord => typeof coord === 'number' && isFinite(coord))) {
              // Assumiamo che boundsToFit sia già nell'ordine corretto [minLon, minLat, maxLon, maxLat]
              // richiesto da MapLibre fitBounds.
              const finalBounds: [number, number, number, number] = boundsToFit;

              // Assicurati che map sia ancora valido prima di chiamare fitBounds
              if (map) {
                 // console.log('[useMapZoomToLayer] Calling fitBounds for layer:', layerIdToZoomTo, 'with bounds:', finalBounds);
                 map.fitBounds(finalBounds, { padding: 100, maxZoom: 16 });
              }
            } else {
              // console.warn(`[useMapZoomToLayer] Tentativo di chiamare fitBounds con valore non valido o non finito per layer ${layerIdToZoomTo}:`, JSON.stringify(boundsToFit));
            }
          } else {
             // console.warn(`[useMapZoomToLayer] Nessun extent valido (boundsToFit is null or invalid) trovato per il layer ${layerIdToZoomTo}. Impossibile eseguire lo zoom.`);
          }

        } catch (error) {
          console.error(`[useMapZoomToLayer] Errore durante lo zoom all'extent per il layer ${layerIdToZoomTo}:`, error); // Mantenuto errore
        } finally {
          // Pulisci sempre la richiesta di zoom
          clearZoomToLayerExtent();
        }
      } else {
        // console.warn(`[useMapZoomToLayer] Layer con ID ${layerIdToZoomTo} non trovato.`); // Già commentato
        // Pulisci la richiesta anche se il layer non è stato trovato
        clearZoomToLayerExtent();
      }
    }
  }, [map, layerIdToZoomTo, getLayerById, clearZoomToLayerExtent]); // Dependencies for the effect
};